import { useEffect, useState } from 'react';
import type { ActionResult } from '@/features/sites/store';

export interface QuantityAndPrice {
  quantity: number;
  id: number;
  materialId: number;
  unitCost: number;
  materialCode: string;
  shortDescription: string;
  unitType: string;
}

interface UseSiteMaterialsProps {
  siteId: number;
  siteMaterials: any[];
  materials: any[];
  onUpdateSiteMaterials: (materials: any[]) => Promise<ActionResult>;
  onDeleteSiteMaterials: (
    siteId: number,
    materialIds: number[]
  ) => Promise<ActionResult>;
}

export const useSiteMaterials = ({
  siteId,
  siteMaterials,
  materials,
  onUpdateSiteMaterials,
  onDeleteSiteMaterials,
}: UseSiteMaterialsProps) => {
  const [localSiteMaterials, setLocalSiteMaterials] = useState<
    QuantityAndPrice[]
  >([]);
  const [addMaterialsOpenModal, setAddMaterialsOpenModal] =
    useState<boolean>(false);

  // Clear materials when siteId changes (new project/site)
  useEffect(() => {
    setLocalSiteMaterials([]);
  }, [siteId]);

  // Transform site materials for table
  useEffect(() => {
    if (siteMaterials.length) {
      const siteMaterialsItems = siteMaterials.map(s => {
        return {
          quantity: s.quantity,
          materialCode: s?.materialName || '',
          id: s?.id || s.materialId,
          materialId: s.materialId || s?.id,
          unitType: s?.unit || '',
          shortDescription: s?.shortDescription || '',
          unitCost: s?.unitCost || 0,
        };
      });
      setLocalSiteMaterials(siteMaterialsItems);
    } else {
      setLocalSiteMaterials([]);
    }
  }, [siteMaterials, materials]);

  // Update single material
  const onUpdateMaterial = async (materialId: number, quantity: number) => {
    // Store original state for potential revert
    const originalSiteMaterials = [...localSiteMaterials];

    // Update local state first
    const newLocalSiteMaterials = [...localSiteMaterials];
    const index = newLocalSiteMaterials.findIndex(
      elem => elem.materialId === materialId
    );
    if (index !== -1) {
      newLocalSiteMaterials[index].quantity = quantity;
      setLocalSiteMaterials(newLocalSiteMaterials);
    }

    // Send all materials to server
    const allMaterialsToUpdate = newLocalSiteMaterials.map(material => ({
      siteId,
      materialId: material.materialId,
      quantity: material.quantity,
      delete: false,
    }));

    const result = await onUpdateSiteMaterials(allMaterialsToUpdate);

    if (!result.success) {
      // Revert local state to original if API call failed
      setLocalSiteMaterials(originalSiteMaterials);
    }
  };

  // Delete single material
  const onDeleteMaterial = async (materialId: number) => {
    const result = await onDeleteSiteMaterials(siteId, [materialId]);

    if (result.success) {
      // Remove from local state
      const newLocalSiteMaterials = localSiteMaterials.filter(
        material => material.materialId !== materialId
      );
      setLocalSiteMaterials(newLocalSiteMaterials);
    }

    return result.success;
  };

  // Delete multiple materials
  const onDeleteMaterials = async (materialIds: number[]) => {
    if (materialIds.length === 0) return true;

    const result = await onDeleteSiteMaterials(siteId, materialIds);

    if (result.success) {
      // Remove from local state
      const newLocalSiteMaterials = localSiteMaterials.filter(
        material => !materialIds.includes(material.materialId)
      );
      setLocalSiteMaterials(newLocalSiteMaterials);
    }

    return result.success;
  };

  // Handle materials change (for bulk operations)
  const onChangeSiteMaterials = async (data: QuantityAndPrice[]) => {
    setLocalSiteMaterials(data);
  };

  // Add all materials from current format
  const addAllMaterials = (project: any, materialsLoading: boolean) => {
    if (materialsLoading || !project?.materialFormat?.id) return;

    const newList: QuantityAndPrice[] = [...localSiteMaterials];

    const filteredItems = materials.filter(
      item => item.materialFormatId == project?.materialFormat?.id
    );

    filteredItems.forEach(item => {
      const material = newList.find(el => el.materialId === item.id);
      if (material) return;
      newList.push({
        id: item.id,
        quantity: 0,
        materialId: item.id,
        unitCost: item.unitCost,
        materialCode: item.materialCode || '',
        shortDescription: item.shortDescription || '',
        unitType: item.unitType || '',
      });
    });

    // Convert to the format expected by updateSiteMaterials
    const materialsToUpdate = newList.map(material => ({
      siteId,
      materialId: material.materialId,
      quantity: material.quantity || 0,
      delete: false,
    }));

    onUpdateSiteMaterials(materialsToUpdate);
    setLocalSiteMaterials(newList);
  };

  // Modal handlers
  const openAddMaterialsModal = (project: any, materialsLoading: boolean) => {
    if (materialsLoading || !project?.materialFormat?.id) return;
    setAddMaterialsOpenModal(true);
  };

  const handleCloseMaterialsModal = () => {
    setAddMaterialsOpenModal(false);
  };

  const handleSaveMaterials = (newList: QuantityAndPrice[]) => {
    // Convert to the format expected by updateSiteMaterials
    const materialsToUpdate = newList.map(material => ({
      siteId,
      materialId: material.materialId,
      quantity: material.quantity || 0,
      delete: false,
    }));

    onUpdateSiteMaterials(materialsToUpdate);
    setLocalSiteMaterials(newList);
  };

  return {
    // State
    localSiteMaterials,
    addMaterialsOpenModal,

    // Actions
    onUpdateMaterial,
    onDeleteMaterial,
    onDeleteMaterials,
    onChangeSiteMaterials,
    addAllMaterials,
    openAddMaterialsModal,
    handleCloseMaterialsModal,
    handleSaveMaterials,
  };
};
